import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings, Layers, Code, Puzzle, ArrowRight, CheckCircle, 
  Play, Star, Smartphone, Monitor, Tablet, Globe, Zap,
  GitBranch, Package, Upload, Download, ChevronRight, Target
} from 'lucide-react';

const CustomDevelopment = () => {
  const [activeFeature, setActiveFeature] = useState('pagebuilder');

  const developmentFeatures = [
    {
      id: 'pagebuilder',
      name: 'Visual Page Builder',
      icon: Layers,
      description: 'Drag-and-drop interface for creating custom application pages',
      capabilities: [
        'Component-based page design with reusable elements',
        'Responsive layout system for all device types',
        'Custom styling and theme management',
        'Interactive form builders with validation',
        'Real-time preview and testing capabilities',
        'Version control and collaboration features'
      ],
      apiEndpoints: [
        { name: 'fetchAllPages', description: 'List all custom pages with pagination' },
        { name: 'fetchPageDetail', description: 'Get detailed page information' },
        { name: 'savePage', description: 'Create and update custom pages' },
        { name: 'deletePage', description: 'Remove custom pages' }
      ],
      examples: [
        'Customer portals with personalized dashboards',
        'Employee self-service applications',
        'Mobile-responsive forms and interfaces',
        'Executive reporting dashboards'
      ]
    },
    {
      id: 'integrations',
      name: 'API Integration Platform',
      icon: Globe,
      description: 'Connect with external services and systems seamlessly',
      capabilities: [
        'REST API integrations with authentication',
        'Webhook management and event handling',
        'Data transformation and mapping pipelines',
        'Real-time synchronization capabilities',
        'Error handling and retry mechanisms',
        'Rate limiting and performance optimization'
      ],
      apiEndpoints: [
        { name: 'invokeFunction', description: 'Execute custom API integrations' },
        { name: 'saveFunction', description: 'Create integration functions' },
        { name: 'compileFunction', description: 'Optimize integration performance' }
      ],
      examples: [
        'CRM system integrations (Salesforce, HubSpot)',
        'Payment gateway connections (Stripe, PayPal)',
        'Third-party service automation (Slack, Teams)',
        'ERP system synchronization (SAP, Oracle)'
      ]
    },
    {
      id: 'packages',
      name: 'Package Management',
      icon: Package,
      description: 'Create, deploy, and manage application packages',
      capabilities: [
        'Component packaging and versioning system',
        'Multi-environment deployment pipelines',
        'Dependency resolution and management',
        'Automated testing and quality assurance',
        'Rollback and recovery mechanisms',
        'Performance monitoring and optimization'
      ],
      apiEndpoints: [
        { name: 'getOrgPackages', description: 'List organization packages' },
        { name: 'createUpdatePackage', description: 'Create and update packages' },
        { name: 'compilePackage', description: 'Compile packages for deployment' },
        { name: 'deletePackage', description: 'Remove packages safely' }
      ],
      examples: [
        'Multi-environment application deployments',
        'Reusable component libraries and templates',
        'Application versioning and rollback strategies',
        'Automated CI/CD pipeline integration'
      ]
    }
  ];

  const componentLibrary = [
    { name: 'Forms & Inputs', icon: '📝', description: 'Rich form controls and validation' },
    { name: 'Data Tables', icon: '📊', description: 'Advanced data grids and lists' },
    { name: 'Charts & Graphs', icon: '📈', description: 'Interactive data visualizations' },
    { name: 'Navigation', icon: '🧭', description: 'Menus, tabs, and breadcrumbs' },
    { name: 'Layout', icon: '📐', description: 'Grids, containers, and panels' },
    { name: 'Buttons & Actions', icon: '🔘', description: 'Interactive buttons and controls' },
    { name: 'Media', icon: '🖼️', description: 'Images, videos, and galleries' },
    { name: 'Feedback', icon: '💬', description: 'Alerts, notifications, and modals' }
  ];

  const developmentTools = [
    {
      name: 'Visual Designer',
      icon: Monitor,
      description: 'Drag-and-drop interface builder',
      features: ['Component palette', 'Property panels', 'Layout tools', 'Style editor']
    },
    {
      name: 'Code Editor',
      icon: Code,
      description: 'Advanced code editing capabilities',
      features: ['Syntax highlighting', 'Auto-completion', 'Debugging tools', 'Version control']
    },
    {
      name: 'Testing Tools',
      icon: Target,
      description: 'Comprehensive testing suite',
      features: ['Unit testing', 'Integration testing', 'Performance testing', 'User testing']
    },
    {
      name: 'Deployment Manager',
      icon: Upload,
      description: 'Automated deployment pipeline',
      features: ['Environment management', 'Rollback capabilities', 'Monitoring tools', 'Performance analytics']
    }
  ];

  const integrationTypes = [
    {
      category: 'CRM Systems',
      icon: '👥',
      services: ['Salesforce', 'HubSpot', 'Microsoft Dynamics', 'Pipedrive']
    },
    {
      category: 'Payment Gateways',
      icon: '💳',
      services: ['Stripe', 'PayPal', 'Square', 'Authorize.Net']
    },
    {
      category: 'Communication',
      icon: '💬',
      services: ['Slack', 'Microsoft Teams', 'Twilio', 'SendGrid']
    },
    {
      category: 'Cloud Storage',
      icon: '☁️',
      services: ['AWS S3', 'Google Drive', 'Dropbox', 'Azure Blob']
    },
    {
      category: 'Analytics',
      icon: '📊',
      services: ['Google Analytics', 'Mixpanel', 'Segment', 'Amplitude']
    },
    {
      category: 'ERP Systems',
      icon: '🏢',
      services: ['SAP', 'Oracle', 'NetSuite', 'QuickBooks']
    }
  ];

  const developmentBenefits = [
    {
      title: 'Faster Development',
      description: 'Build applications 10x faster than traditional coding',
      icon: Zap,
      metrics: ['90% faster delivery', '50% lower costs', 'Rapid prototyping']
    },
    {
      title: 'No-Code Flexibility',
      description: 'Empower business users to create applications',
      icon: Settings,
      metrics: ['Zero coding required', 'Business user friendly', 'IT governance']
    },
    {
      title: 'Enterprise Ready',
      description: 'Built for scale, security, and performance',
      icon: CheckCircle,
      metrics: ['Enterprise security', 'Scalable architecture', 'High availability']
    },
    {
      title: 'Future Proof',
      description: 'Evolve with changing business requirements',
      icon: GitBranch,
      metrics: ['Easy updates', 'Version control', 'Migration tools']
    }
  ];

  const applicationTypes = [
    {
      type: 'Customer Portals',
      icon: '🌐',
      description: 'Self-service customer applications',
      features: ['Account management', 'Order tracking', 'Support tickets', 'Knowledge base']
    },
    {
      type: 'Employee Apps',
      icon: '👔',
      description: 'Internal workforce applications',
      features: ['HR self-service', 'Expense reporting', 'Time tracking', 'Performance reviews']
    },
    {
      type: 'Mobile Apps',
      icon: '📱',
      description: 'Native mobile experiences',
      features: ['Responsive design', 'Offline capabilities', 'Push notifications', 'Device integration']
    },
    {
      type: 'Dashboards',
      icon: '📊',
      description: 'Executive and operational dashboards',
      features: ['Real-time data', 'Interactive charts', 'Drill-down analysis', 'Export capabilities']
    }
  ];

  const testimonials = [
    {
      quote: "The visual page builder allowed our business team to create customer portals without any IT involvement. Game changer!",
      name: "",
      title: "",
      company: "Digital Solutions Inc",
      avatar: "👩‍💼",
      metric: "5 apps built in 2 weeks"
    },
    {
      quote: "API integrations that used to take months now take days. The no-code approach is incredibly powerful.",
      name: "",
      title: "",
      company: "TechFlow Systems",
      avatar: "👨‍💻",
      metric: "20+ integrations deployed"
    },
    {
      quote: "Package management made our deployment process seamless. We can now update applications across environments instantly.",
      name: "",
      title: "",
      company: "CloudFirst Corp",
      avatar: "👩‍💻",
      metric: "Zero deployment failures"
    }
  ];

  const stats = [
                { number: 'Global', label: 'Custom Apps Built', description: 'Across all client organizations' },
    { number: '90%', label: 'Faster Development', description: 'Compared to traditional coding' },
    { number: '100+', label: 'Integration Connectors', description: 'Pre-built service connections' },
    { number: '99.9%', label: 'Application Uptime', description: 'Enterprise-grade reliability' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto text-white"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white mb-6">
              <Settings className="h-10 w-10" />
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Custom Development
              <span className="block text-3xl lg:text-4xl font-normal mt-2 text-indigo-200">
                No-Code Application Platform
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/80 mb-10 leading-relaxed">
              Build powerful custom applications without coding using our visual development platform, 
              extensive integrations, and enterprise-grade deployment tools.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Try Development Platform
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="https://www.youtube.com/@nocodeAquargin"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Builder Demo
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center bg-white p-6 rounded-xl shadow-lg"
              >
                <div className="text-4xl font-bold text-indigo-600 mb-2">{stat.number}</div>
                <div className="text-gray-900 font-semibold mb-1">{stat.label}</div>
                <div className="text-sm text-gray-600">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Development Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Development Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete no-code development environment for building enterprise applications
            </p>
          </div>

          {/* Feature Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {developmentFeatures.map((feature) => (
              <button
                key={feature.id}
                onClick={() => setActiveFeature(feature.id)}
                className={`flex items-center space-x-3 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeFeature === feature.id
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <feature.icon className="h-5 w-5" />
                <span>{feature.name}</span>
              </button>
            ))}
          </div>

          {/* Active Feature Content */}
          {developmentFeatures.map((feature) => (
            activeFeature === feature.id && (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-6xl mx-auto"
              >
                <div className="grid lg:grid-cols-2 gap-12">
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-4">
                        <feature.icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">{feature.name}</h3>
                    </div>
                    <p className="text-xl text-gray-600 mb-8">{feature.description}</p>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        Key Capabilities
                      </h4>
                      <ul className="space-y-3">
                        {feature.capabilities.map((capability, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700">{capability}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div className="bg-gray-50 p-6 rounded-2xl">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Code className="h-5 w-5 text-indigo-500 mr-2" />
                        API Endpoints
                      </h4>
                      <div className="space-y-3">
                        {feature.apiEndpoints.map((endpoint, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg border-l-4 border-indigo-500">
                            <div className="font-mono text-sm text-indigo-600 font-semibold">
                              {endpoint.name}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              {endpoint.description}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-indigo-50 p-6 rounded-2xl">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Target className="h-5 w-5 text-indigo-500 mr-2" />
                        Example Applications
                      </h4>
                      <div className="space-y-3">
                        {feature.examples.map((example, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <ChevronRight className="h-5 w-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-700">{example}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          ))}
        </div>
      </section>

      {/* Component Library */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Component Library
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Rich set of pre-built components for rapid application development
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {componentLibrary.map((component, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow text-center"
              >
                <div className="text-4xl mb-3">{component.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{component.name}</h3>
                <p className="text-sm text-gray-600">{component.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Development Tools */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Development Tools
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional-grade tools for building enterprise applications
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {developmentTools.map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-4">
                    <tool.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{tool.name}</h3>
                    <p className="text-gray-600">{tool.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {tool.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Types */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Pre-built Integrations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Connect with popular services and platforms out of the box
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {integrationTypes.map((integration, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  <div className="text-3xl mr-3">{integration.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900">{integration.category}</h3>
                </div>
                
                <div className="space-y-2">
                  {integration.services.map((service, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                      <span className="text-gray-700">{service}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Types */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Application Types
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Build any type of business application with our flexible platform
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {applicationTypes.map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4">{app.icon}</div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{app.type}</h3>
                    <p className="text-gray-600">{app.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {app.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Puzzle className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Development Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Development Benefits
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Why choose no-code development for your enterprise applications
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {developmentBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-4">
                    <benefit.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  {benefit.metrics.map((metric, idx) => (
                    <span key={idx} className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-semibold">
                      {metric}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Development Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how organizations accelerated development with our platform
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 mb-6 text-lg leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-3xl mr-3">{testimonial.avatar}</div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.title}</div>
                      <div className="text-sm text-gray-500">{testimonial.company}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-indigo-600">{testimonial.metric}</div>
                    <div className="text-xs text-gray-500">Achievement</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Start Building Today
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Experience the power of no-code development and build your first 
              enterprise application in minutes, not months.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Start Building Free
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <Settings className="mr-2 h-5 w-5" />
                Schedule Builder Demo
              </motion.a>
            </div>

            <div className="mt-8 text-white/80 text-sm">
              <p>✓ No credit card required  ✓ Full development access  ✓ 24/7 support included</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CustomDevelopment; 